import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/http";
import {
  commerceAdmin,
  type ProductCreate,
  type ShopProduct,
  type SkuCreate,
} from "@/api/commerceAdmin";
import { Modal } from "@/components/Modal";
import { useToast } from "@/components/Toast";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  FormError,
  Input,
  Label,
  Select,
} from "@/components/ui";

function errMsg(e: unknown) {
  return e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e);
}

const PRODUCT_KINDS = ["robot", "signal", "course", "indicator", "data_feed"];

/**
 * Shop product admin — manage the global digital add-on catalogue (products +
 * SKUs) and manually grant entitlements (comps / support). Note: products are
 * platform-global (org_id NULL); requires the `shop:manage` permission.
 */
export function AdminShopPage() {
  const products = useQuery({ queryKey: ["shop-admin-products"], queryFn: () => commerceAdmin.listProducts() });
  const [newProduct, setNewProduct] = useState(false);
  const [skuFor, setSkuFor] = useState<ShopProduct | null>(null);
  const [granting, setGranting] = useState(false);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h1 className="text-base font-semibold text-term-fg">Shop products</h1>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={() => setGranting(true)}>
            Grant entitlement
          </Button>
          <Button onClick={() => setNewProduct(true)}>New product</Button>
        </div>
      </div>

      {products.isLoading ? (
        <div className="p-3 text-sm text-term-mute">Loading…</div>
      ) : !products.data || products.data.length === 0 ? (
        <Card>
          <CardBody>
            <EmptyState title="No products" description="Create digital add-ons (robots, signals, courses) to sell." />
          </CardBody>
        </Card>
      ) : (
        <div className="space-y-3">
          {products.data.map((p) => (
            <Card key={p.id}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-medium text-term-fg">{p.name}</span>
                    <span className="ml-2 font-mono text-2xs text-term-dim">{p.slug}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge tone="neutral">{p.kind}</Badge>
                    <Badge tone={p.status === "active" ? "good" : "neutral"}>{p.status}</Badge>
                    <Button variant="ghost" onClick={() => setSkuFor(p)}>
                      Add SKU
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardBody className="p-0">
                {p.skus.length === 0 ? (
                  <div className="p-3 text-2xs text-term-mute">No SKUs — add one to make it purchasable.</div>
                ) : (
                  <table className="w-full text-xs">
                    <thead className="bg-term-elev text-term-mute">
                      <tr>
                        <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">SKU</th>
                        <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Price</th>
                        <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Billing</th>
                        <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Entitlement</th>
                      </tr>
                    </thead>
                    <tbody>
                      {p.skus.map((s) => (
                        <tr key={s.id} className="border-t border-term-border">
                          <td className="px-3 py-1.5 font-mono text-2xs text-term-dim">{s.sku_code}</td>
                          <td className="px-3 py-1.5 text-right tnum text-term-fg">
                            {(s.price_cents / 100).toFixed(2)} {s.currency}
                          </td>
                          <td className="px-3 py-1.5 text-2xs text-term-dim">
                            {s.billing_type}
                            {s.duration_days ? ` · ${s.duration_days}d` : ""}
                          </td>
                          <td className="px-3 py-1.5 text-2xs text-term-dim">
                            {s.entitlement_kind}
                            {s.entitlement_ref ? `:${s.entitlement_ref}` : ""}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      {newProduct && <ProductModal onClose={() => setNewProduct(false)} />}
      {skuFor && <SkuModal product={skuFor} onClose={() => setSkuFor(null)} />}
      {granting && <GrantModal onClose={() => setGranting(false)} />}
    </div>
  );
}

function ProductModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState<ProductCreate>({
    slug: "",
    kind: PRODUCT_KINDS[0],
    name: "",
    summary: "",
    status: "draft",
  });
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => commerceAdmin.createProduct(form),
    onSuccess: () => {
      toast.push("Product created", "success");
      qc.invalidateQueries({ queryKey: ["shop-admin-products"] });
      onClose();
    },
    onError: (e) => setErr(errMsg(e)),
  });

  return (
    <Modal open onClose={onClose} title="New product">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErr(null);
          create.mutate();
        }}
        className="space-y-3"
      >
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label>Slug</Label>
            <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="pro-signals" />
          </div>
          <div>
            <Label>Kind</Label>
            <Select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
              {PRODUCT_KINDS.map((k) => (
                <option key={k} value={k}>{k}</option>
              ))}
            </Select>
          </div>
        </div>
        <div>
          <Label>Name</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </div>
        <div>
          <Label>Summary</Label>
          <Input value={form.summary ?? ""} onChange={(e) => setForm({ ...form, summary: e.target.value })} />
        </div>
        <div>
          <Label>Status</Label>
          <Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="retired">Retired</option>
          </Select>
        </div>
        <FormError>{err}</FormError>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={create.isPending}>
            Create
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function SkuModal({ product, onClose }: { product: ShopProduct; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState<SkuCreate>({
    sku_code: "",
    name: "",
    price_cents: 0,
    currency: "USD",
    billing_type: "one_time",
    entitlement_kind: product.kind,
    active: true,
  });
  const [priceStr, setPriceStr] = useState("0");
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      commerceAdmin.createSku(product.id, {
        ...form,
        price_cents: Math.round(parseFloat(priceStr || "0") * 100),
      }),
    onSuccess: () => {
      toast.push("SKU created", "success");
      qc.invalidateQueries({ queryKey: ["shop-admin-products"] });
      onClose();
    },
    onError: (e) => setErr(errMsg(e)),
  });

  return (
    <Modal open onClose={onClose} title={`Add SKU · ${product.name}`}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErr(null);
          create.mutate();
        }}
        className="space-y-3"
      >
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label>SKU code</Label>
            <Input value={form.sku_code} onChange={(e) => setForm({ ...form, sku_code: e.target.value })} />
          </div>
          <div>
            <Label>Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <Label>Price</Label>
            <Input value={priceStr} onChange={(e) => setPriceStr(e.target.value)} placeholder="9.99" />
          </div>
          <div>
            <Label>Currency</Label>
            <Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} />
          </div>
          <div>
            <Label>Billing</Label>
            <Select value={form.billing_type} onChange={(e) => setForm({ ...form, billing_type: e.target.value })}>
              <option value="one_time">One-time</option>
              <option value="subscription">Subscription</option>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label>Entitlement kind</Label>
            <Input
              value={form.entitlement_kind}
              onChange={(e) => setForm({ ...form, entitlement_kind: e.target.value })}
            />
          </div>
          <div>
            <Label>Duration (days, blank = perpetual)</Label>
            <Input
              value={form.duration_days != null ? String(form.duration_days) : ""}
              onChange={(e) =>
                setForm({ ...form, duration_days: e.target.value ? parseInt(e.target.value, 10) : null })
              }
            />
          </div>
        </div>
        <FormError>{err}</FormError>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={create.isPending}>
            Add SKU
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function GrantModal({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const [form, setForm] = useState({ user_id: "", sku_code: "", entitlement_kind: "", duration_days: "" });
  const [err, setErr] = useState<string | null>(null);

  const grant = useMutation({
    mutationFn: () =>
      commerceAdmin.grant({
        user_id: form.user_id,
        sku_code: form.sku_code,
        entitlement_kind: form.entitlement_kind,
        duration_days: form.duration_days ? parseInt(form.duration_days, 10) : null,
      }),
    onSuccess: () => {
      toast.push("Entitlement granted", "success");
      onClose();
    },
    onError: (e) => setErr(errMsg(e)),
  });

  return (
    <Modal open onClose={onClose} title="Grant entitlement">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErr(null);
          grant.mutate();
        }}
        className="space-y-3"
      >
        <div>
          <Label>User id</Label>
          <Input value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label>SKU code</Label>
            <Input value={form.sku_code} onChange={(e) => setForm({ ...form, sku_code: e.target.value })} />
          </div>
          <div>
            <Label>Entitlement kind</Label>
            <Input
              value={form.entitlement_kind}
              onChange={(e) => setForm({ ...form, entitlement_kind: e.target.value })}
            />
          </div>
        </div>
        <div>
          <Label>Duration (days, blank = perpetual)</Label>
          <Input
            value={form.duration_days}
            onChange={(e) => setForm({ ...form, duration_days: e.target.value })}
          />
        </div>
        <FormError>{err}</FormError>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={grant.isPending}>
            Grant
          </Button>
        </div>
      </form>
    </Modal>
  );
}
